import {
  BREW_METHODS,
  type BrewKey,
  formatMoney,
  ROASTS,
  type RoastKey,
  unitPrice,
  WEIGHTS,
  type WeightKey,
} from '@c214/shared';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { PackArt } from '../components/product/PackArt';
import { Button } from '../components/ui/button';
import { Chip } from '../components/ui/chip';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  type AdminProduct,
  type ProductFormInput,
  useAdminProduct,
  useSaveProduct,
  useSetProductActive,
} from '../hooks/useAdminProducts';
import { useAuth } from '../hooks/useAuth';
import type { ApiError } from '../lib/api';
import { useAuthModal } from '../providers';

interface FormState {
  name: string;
  packSubtitle: string;
  region: string;
  roast: RoastKey;
  brew: BrewKey[];
  price: string;
  score: string;
  altitude: string;
  notes: string;
  description: string;
  acid: string;
  bitter: string;
  body: string;
  roastedAt: string;
  shipsAt: string;
  inSubscription: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): FormState => ({
  name: '',
  packSubtitle: '',
  region: '',
  roast: 'medium',
  brew: ['filter'],
  price: '',
  score: '85.0',
  altitude: '',
  notes: '',
  description: '',
  acid: '5',
  bitter: '5',
  body: '5',
  roastedAt: today(),
  shipsAt: today(),
  inSubscription: false,
});

function toForm(product: AdminProduct): FormState {
  return {
    name: product.name,
    packSubtitle: product.packSubtitle,
    region: product.region,
    roast: product.roast,
    brew: product.brew,
    price: String(product.price),
    score: product.score,
    altitude: product.altitude,
    notes: product.notes,
    description: product.description,
    acid: String(product.acid),
    bitter: String(product.bitter),
    body: String(product.body),
    roastedAt: product.roastedAt.slice(0, 10),
    shipsAt: product.shipsAt.slice(0, 10),
    inSubscription: product.inSubscription,
  };
}

// Порожній/пробільний рядок → NaN замість 0, щоб не зберегти незаповнене поле
// як справжній нуль: NaN серіалізується в JSON як null, а null провалює zod-схему
// й підсвічує саме це поле (замість мовчазного 0).
function toNumber(value: string): number {
  return value.trim() === '' ? Number.NaN : Number(value);
}

// Числові поля форми — рядки (щоб не заважати набору); сервер перевіряє діапазони.
function toInput(form: FormState): ProductFormInput {
  return {
    ...form,
    price: toNumber(form.price),
    acid: toNumber(form.acid),
    bitter: toNumber(form.bitter),
    body: toNumber(form.body),
  };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children is always an Input/Textarea (a real <input>/<textarea> at runtime), biome just can't see through the wrapper component
    <label className="block space-y-1">
      <span className="mono-label">{label}</span>
      {children}
    </label>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="m-0 space-y-3 border-0 border-t border-line-soft p-0 pt-5">
      <legend className="mono-label mb-2 text-ink">{title}</legend>
      {children}
    </fieldset>
  );
}

export function AdminProductEditPage() {
  const { id } = useParams();
  const productId = id === undefined ? undefined : Number(id);
  const isNew = productId === undefined;
  const navigate = useNavigate();
  const { data: user, isLoading: authLoading } = useAuth();
  const authModal = useAuthModal();
  const { data: product, isLoading, isError } = useAdminProduct(productId);
  const save = useSaveProduct();
  const setActive = useSetProductActive();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [packWeight, setPackWeight] = useState<WeightKey>('250');
  // Reset the form only when a *different* product loads — not on every refetch
  // (archive/restore refetches this same product and would otherwise wipe
  // whatever the admin was still typing).
  const [loadedProductId, setLoadedProductId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (product && product.id !== loadedProductId) {
      setForm(toForm(product));
      setLoadedProductId(product.id);
    }
  }, [product, loadedProductId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const errorField = (save.error as ApiError | null)?.field;
  const invalid = (key: keyof FormState) => (errorField === key ? 'border-warn' : '');

  const toggleBrew = (method: BrewKey) =>
    set(
      'brew',
      form.brew.includes(method) ? form.brew.filter((m) => m !== method) : [...form.brew, method],
    );

  const submit = () =>
    save.mutate(
      { id: productId, input: toInput(form) },
      {
        onSuccess: (saved) => {
          if (isNew) navigate(`/admin/products/${saved.id}`, { replace: true });
        },
      },
    );

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;
  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб керувати товарами.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  if (!isNew && isLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;
  if (!isNew && (isError || !product)) {
    return (
      <div className="section-pad">
        <p className="text-[15px] text-muted">Товар не знайдено.</p>
        <Link to="/admin/products" className="mono-label no-underline">
          ← ТОВАРИ
        </Link>
      </div>
    );
  }

  const price = Number(form.price) || 0;

  return (
    <div className="section-pad">
      <Link to="/admin/products" className="mono-label no-underline">
        ← ТОВАРИ
      </Link>
      <h1 className="m-0 mb-8 mt-3 text-[clamp(28px,5vw,56px)] font-extrabold tracking-[-0.045em]">
        {isNew ? 'Новий товар' : form.name || 'Товар'}
        {product && !product.isActive && (
          <span className="mono-label ml-4 align-middle text-warn">В АРХІВІ</span>
        )}
      </h1>

      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4 self-start lg:sticky lg:top-24 lg:order-2">
          <div className="flex justify-center bg-surface p-6">
            <PackArt
              name={form.name || 'Назва товару'}
              packSubtitle={form.packSubtitle}
              roast={form.roast}
              notes={form.notes}
              roastedAt={form.roastedAt || today()}
              weight={packWeight}
              className="h-[380px] w-auto max-w-full"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {WEIGHTS.map((weight) => (
              <Chip
                key={weight.key}
                active={packWeight === weight.key}
                onClick={() => setPackWeight(weight.key)}
              >
                {weight.label}
              </Chip>
            ))}
          </div>
          <ul className="m-0 list-none space-y-1 p-0 font-mono text-[13px] text-muted">
            {WEIGHTS.map((weight) => (
              <li key={weight.key}>
                {weight.label} — {formatMoney(unitPrice(price, weight.key))}
              </li>
            ))}
          </ul>
        </div>

        <div className="max-w-[640px] space-y-6 lg:order-1">
          <Block title="НАЗВА Й ПАЧКА">
            <Field label="Назва">
              <Input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                className={invalid('name')}
              />
            </Field>
            <Field label="Рядок на пачці">
              <Input
                value={form.packSubtitle}
                onChange={(e) => set('packSubtitle', e.target.value)}
                placeholder="ETHIOPIA · YIRGACHEFFE"
                maxLength={30}
                className={invalid('packSubtitle')}
              />
            </Field>
            <Field label="Регіон">
              <Input
                value={form.region}
                onChange={(e) => set('region', e.target.value)}
                className={invalid('region')}
              />
            </Field>
            <div className="space-y-1">
              <span className="mono-label">Обсмаження</span>
              <div className="flex flex-wrap gap-2">
                {ROASTS.map((roast) => (
                  <Chip
                    key={roast.key}
                    active={form.roast === roast.key}
                    onClick={() => set('roast', roast.key)}
                  >
                    {roast.label}
                  </Chip>
                ))}
              </div>
            </div>
            <Field label="Ноти (через кому)">
              <Input
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="жасмин, лимон, чорний чай"
                className={invalid('notes')}
              />
            </Field>
          </Block>

          <Block title="ЦІНА Й ПІДПИСКА">
            <Field label="Ціна за 250 г, ₴">
              <Input
                type="number"
                min={1}
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                className={invalid('price')}
              />
            </Field>
            <Chip
              active={form.inSubscription}
              onClick={() => set('inSubscription', !form.inSubscription)}
            >
              {form.inSubscription ? '✓ Доступний у підписці' : 'Не в підписці'}
            </Chip>
          </Block>

          <Block title="СМАК">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Оцінка SCA">
                <Input
                  value={form.score}
                  onChange={(e) => set('score', e.target.value)}
                  className={invalid('score')}
                />
              </Field>
              <Field label="Висота">
                <Input
                  value={form.altitude}
                  onChange={(e) => set('altitude', e.target.value)}
                  placeholder="1 950 м"
                  className={invalid('altitude')}
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {(['acid', 'bitter', 'body'] as const).map((key) => (
                <Field
                  key={key}
                  label={{ acid: 'Кислинка 0–10', bitter: 'Гірчинка 0–10', body: 'Тіло 0–10' }[key]}
                >
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    className={invalid(key)}
                  />
                </Field>
              ))}
            </div>
            <div className="space-y-1">
              <span className="mono-label">Методи заварювання</span>
              <div className="flex flex-wrap gap-2">
                {BREW_METHODS.map((method) => (
                  <Chip
                    key={method.key}
                    active={form.brew.includes(method.key)}
                    onClick={() => toggleBrew(method.key)}
                  >
                    {method.label}
                  </Chip>
                ))}
              </div>
            </div>
          </Block>

          <Block title="ОПИС">
            <Textarea
              rows={6}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className={invalid('description')}
            />
          </Block>

          <Block title="ДАТИ">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Обсмажено">
                <Input
                  type="date"
                  value={form.roastedAt}
                  onChange={(e) => set('roastedAt', e.target.value)}
                  className={invalid('roastedAt')}
                />
              </Field>
              <Field label="Відправка">
                <Input
                  type="date"
                  value={form.shipsAt}
                  onChange={(e) => set('shipsAt', e.target.value)}
                  className={invalid('shipsAt')}
                />
              </Field>
            </div>
          </Block>

          {save.isError && <p className="text-[13px] text-warn">{save.error.message}</p>}
          {save.isSuccess && <p className="text-[13px] text-accent">Збережено.</p>}

          <div className="flex flex-wrap gap-3">
            <Button variant="accent" size="lg" disabled={save.isPending} onClick={submit}>
              ЗБЕРЕГТИ
            </Button>
            {product && (
              <Button
                variant="secondary"
                size="lg"
                disabled={setActive.isPending}
                onClick={() => setActive.mutate({ id: product.id, active: !product.isActive })}
              >
                {product.isActive ? 'В АРХІВ' : 'ПОВЕРНУТИ З АРХІВУ'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
